import { useState } from "react";
import { toast } from "sonner";
import { Bell, Globe, Volume2, Info } from "lucide-react";
import api from "@/config/api";
import { useAuth } from "@/contexts/AuthContext";
import { useSeo } from "@/hooks/useSeo";

const Toggle = ({ on, onClick, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className={`w-12 h-7 rounded-full transition-colors relative shrink-0 ${on ? "bg-primary" : "bg-slate-200"}`}>
    <span className={`absolute top-1 w-5 h-5 rounded-full bg-white shadow transition-all ${on ? "left-6" : "left-1"}`} />
  </button>
);

const LANGS = [{ id: "fr", label: "Français" }, { id: "ar", label: "العربية" }, { id: "en", label: "English" }];

export default function Settings() {
  useSeo("Paramètres", "Personnalise Quran Coach : rappels quotidiens, langue de l'interface et récitateur.");
  const { user, setUser } = useAuth();
  const [reminders, setReminders] = useState(user?.preferences?.reminders ?? true);
  const [lang, setLang] = useState(user?.language || "fr");

  const savePref = async (patch) => {
    try {
      const { data } = await api.put("/profile", patch);
      setUser(data);
    } catch { toast.error("Erreur de sauvegarde."); }
  };

  const toggleReminders = () => {
    const v = !reminders; setReminders(v);
    savePref({ preferences: { ...(user?.preferences || {}), reminders: v } });
    toast.success(v ? "Rappels activés 🔔" : "Rappels désactivés");
  };

  const changeLang = (id) => {
    setLang(id);
    savePref({ language: id });
    toast.success("Langue enregistrée (interface FR pour cette version).");
  };

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h1 className="text-3xl font-black text-slate-800">Paramètres</h1>
        <p className="text-slate-500">Personnalise ton expérience.</p>
      </div>

      <div className="bg-white rounded-3xl border border-slate-100 shadow-[0_4px_20px_rgba(0,0,0,0.03)] divide-y divide-slate-100">
        <div className="flex items-center gap-4 p-5">
          <div className="w-11 h-11 rounded-2xl bg-accent/15 text-yellow-600 flex items-center justify-center"><Bell className="w-5 h-5" /></div>
          <div className="flex-1">
            <div className="font-extrabold text-slate-800">Rappels quotidiens</div>
            <div className="text-sm text-slate-400">Recevoir un rappel pour ta séance</div>
          </div>
          <Toggle on={reminders} onClick={toggleReminders} testid="toggle-reminders" />
        </div>

        <div className="p-5">
          <div className="flex items-center gap-4 mb-3">
            <div className="w-11 h-11 rounded-2xl bg-primary/10 text-primary flex items-center justify-center"><Globe className="w-5 h-5" /></div>
            <div>
              <div className="font-extrabold text-slate-800">Langue</div>
              <div className="text-sm text-slate-400">Interface principale (FR par défaut)</div>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {LANGS.map((l) => (
              <button key={l.id} onClick={() => changeLang(l.id)} data-testid={`lang-${l.id}`}
                className={`py-2.5 rounded-2xl font-bold text-sm border-2 transition-colors ${lang === l.id ? "border-primary bg-primary/10 text-primary" : "border-slate-200 text-slate-500"}`}>
                {l.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-4 p-5">
          <div className="w-11 h-11 rounded-2xl bg-secondary/10 text-secondary flex items-center justify-center"><Volume2 className="w-5 h-5" /></div>
          <div className="flex-1">
            <div className="font-extrabold text-slate-800">Récitateur</div>
            <div className="text-sm text-slate-400">Mishary Al-Afasy</div>
          </div>
        </div>
      </div>

      <div className="flex items-start gap-2 bg-slate-100 rounded-2xl px-4 py-3 text-slate-500 text-sm">
        <Info className="w-4 h-4 mt-0.5 shrink-0" />
        <span>Données coraniques fournies par AlQuran Cloud (texte, traduction française Hamidullah, audio Al-Afasy).</span>
      </div>
    </div>
  );
}
